import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from "recharts";
import { Loader2, TrendingUp, ShieldCheck, FileDown, FileSpreadsheet, Building2, Euro, AlertTriangle, RefreshCw } from "lucide-react";

const fmtEur = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const fmtPp = (n) => `${Number(n || 0) > 0 ? "+" : ""}${Number(n || 0).toFixed(1)} pp`;
const fmtRel = (n) => (n === null || n === undefined ? "—" : `${Number(n) > 0 ? "+" : ""}${Number(n).toFixed(1)}%`);

export default function ConsultancyImprovementsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [metric, setMetric] = useState("eur"); // "eur" | "units"
  const [exporting, setExporting] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/admin/consultancy-improvements", {
        params: refresh ? { refresh: 1 } : undefined,
        timeout: 180000,
      });
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "El cálculo ha tardado demasiado. Vuelve a intentarlo." : null)
        || "No se pudieron cargar las mejoras";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get("/admin/consultancy-improvements/export", {
        params: { format: fmt },
        responseType: "blob",
        timeout: 120000,
      });
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mejoras_archiduque_consulting.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("No se pudo exportar");
    } finally {
      setExporting(null);
    }
  };

  const isEur = metric === "eur";
  const totals = data?.totals;
  const pharmacies = data?.pharmacies || [];

  const barData = useMemo(
    () => pharmacies.slice(0, 25).map((p) => ({
      name: p.anon.replace("Farmacia ", "F"),
      delta: isEur ? p.delta_eur_pp : p.delta_units_pp,
      rel: (isEur ? p.relative_improvement_pct : p.relative_improvement_units_pct) || 0,
      euros: p.euros_moved_year,
    })),
    [pharmacies, isEur],
  );

  const lineData = useMemo(
    () => (data?.timeline || []).map((t) => ({
      month: t.month,
      pct: isEur ? t.avg_generic_pct_eur : t.avg_generic_pct_units,
      studies: t.studies,
    })),
    [data, isEur],
  );

  if (loading) {
    return (
      <div className="p-8 text-slate-500 flex items-center gap-2" data-testid="consultancy-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Calculando la mejora de las farmacias…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="consultancy-improvements-page">
      <header className="bg-slate-900 text-white rounded-2xl p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-sky-300 font-bold flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> Archiduque Consulting
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">Mejoras conseguidas</h1>
            <p className="text-slate-300 text-base md:text-lg mt-2 max-w-3xl">
              Evolución real de las farmacias que suben estudios: comparamos su <strong className="text-white">primer
              estudio</strong> con su <strong className="text-white">mejor resultado</strong> alcanzado. Si después
              bajaron, la bajada no se tiene en cuenta y solo se incluyen las farmacias que mejoran. Material 100%
              anónimo para presentar el servicio de consultoría.
            </p>
            <div className="inline-flex items-center gap-1.5 mt-3 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
              <ShieldCheck className="h-3 w-3" /> Sin nombres, sin provincia, sin licencias
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex bg-white/10 rounded-lg p-1" data-testid="consultancy-metric-toggle">
              {[["eur", "% sobre €"], ["units", "% sobre unidades"]].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setMetric(k)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${metric === k ? "bg-white text-slate-900" : "text-slate-200 hover:text-white"}`}
                  data-testid={`consultancy-metric-${k}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => load(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
                data-testid="consultancy-refresh"
                title="Recalcular con los últimos estudios subidos"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Recalcular
              </button>
              <button
                onClick={() => doExport("pdf")}
                disabled={exporting === "pdf"}
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-60 text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
                data-testid="consultancy-export-pdf"
              >
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
              </button>
              <button
                onClick={() => doExport("xlsx")}
                disabled={exporting === "xlsx"}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
                data-testid="consultancy-export-xlsx"
              >
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
              </button>
            </div>
          </div>
        </div>
      </header>

      {loadError ? (
        <section className="bg-white border border-rose-200 rounded-2xl p-8 text-center" data-testid="consultancy-error">
          <AlertTriangle className="h-8 w-8 text-rose-400 mx-auto mb-2" />
          <h2 className="font-display text-xl font-bold">No se han podido cargar las mejoras</h2>
          <p className="text-slate-500 text-sm mt-1">{loadError}</p>
          <button
            onClick={() => load(true)}
            className="mt-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="consultancy-retry"
          >
            <RefreshCw className="h-4 w-4" /> Reintentar
          </button>
        </section>
      ) : !totals || totals.pharmacies === 0 ? (
        <section className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="consultancy-empty">
          <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <h2 className="font-display text-xl font-bold">Aún no hay farmacias con mejora</h2>
          <p className="text-slate-500 text-sm mt-1">
            Se necesitan al menos 2 estudios de la misma farmacia y que el mejor resultado supere al estudio base.
            {totals?.pharmacies_single_study ? ` Ahora mismo hay ${totals.pharmacies_single_study} con un solo estudio.` : ""}
            {totals?.pharmacies_excluded_negative ? ` ${totals.pharmacies_excluded_negative} se han excluido por no mejorar.` : ""}
          </p>
        </section>
      ) : (
        <>
          <section className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="consultancy-kpis">
            <Kpi label="Farmacias con mejora" value={totals.pharmacies} sub={totals.pharmacies_excluded_negative ? `${totals.pharmacies_excluded_negative} excluidas sin mejora` : "todas mejoran"} />
            <Kpi label="% Genérico medio (base)" value={`${(isEur ? totals.avg_first_generic_pct_eur : totals.avg_first_generic_pct_units).toFixed(1)}%`} sub="primer estudio" />
            <Kpi label="% Genérico medio (mejor)" value={`${(isEur ? totals.avg_last_generic_pct_eur : totals.avg_last_generic_pct_units).toFixed(1)}%`} sub="mejor resultado alcanzado" accent="sky" />
            <Kpi label="Mejora media (pp)" value={fmtPp(isEur ? totals.avg_delta_eur_pp : totals.avg_delta_units_pp)} sub={`mejor farmacia ${fmtPp(totals.best_delta_eur_pp)} (€)`} accent="emerald" />
            <Kpi label="Mejora media relativa" value={fmtRel(isEur ? totals.avg_relative_improvement_pct : totals.avg_relative_improvement_units_pct)} sub="sobre su punto de partida" accent="emerald" />
            <Kpi label="€ convertidos a genérico" value={fmtEur(totals.total_euros_moved_year)} sub={`${fmtEur(totals.total_euros_moved_month)} al mes`} accent="emerald" Icon={Euro} />
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="consultancy-timeline">
            <h2 className="font-display text-xl font-bold tracking-tight">Evolución del % genérico en el tiempo</h2>
            <p className="text-slate-500 text-sm mb-4">
              Media de las farmacias por mes de estudio, del arranque hasta su mejor resultado
              ({isEur ? "sobre facturación €" : "sobre unidades"}).
            </p>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={lineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip formatter={(v) => [`${v}%`, "% genérico medio"]} />
                  <Legend />
                  <Line type="monotone" dataKey="pct" name="% genérico medio" stroke="#0ea5e9" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="consultancy-bars">
            <h2 className="font-display text-xl font-bold tracking-tight">Mejora por farmacia</h2>
            <p className="text-slate-500 text-sm mb-4">
              En <strong>puntos porcentuales</strong> (verde) y en <strong>% relativo</strong> sobre su punto de partida
              (azul). Ej.: de 40% a 55% son +15 pp, que equivalen a un <strong>+37,5%</strong> más de genérico.
            </p>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, k) => (k === "Mejora relativa (%)" ? [`${v}%`, k] : [`${v} pp`, k])} />
                  <Legend />
                  <Bar dataKey="delta" name="Mejora (pp)" fill="#10b981" radius={[4, 4, 0, 0]}>
                    {barData.map((d, i) => (
                      <Cell key={i} fill={d.delta >= 0 ? "#10b981" : "#e11d48"} />
                    ))}
                  </Bar>
                  <Bar dataKey="rel" name="Mejora relativa (%)" radius={[4, 4, 0, 0]} fill="#0ea5e9" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="consultancy-table">
            <h2 className="font-display text-xl font-bold tracking-tight">Detalle anónimo</h2>
            <p className="text-slate-500 text-sm mb-4">
              Estudio base vs mejor resultado alcanzado. «€ convertidos» = facturación de marca que ha pasado a genérico.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                    <th className="text-left py-2.5">Farmacia</th>
                    <th className="text-right py-2.5">Estudios</th>
                    <th className="text-left py-2.5">Base</th>
                    <th className="text-left py-2.5">Mejor</th>
                    <th className="text-right py-2.5">% Gen base</th>
                    <th className="text-right py-2.5">% Gen mejor</th>
                    <th className="text-right py-2.5">Mejora pp</th>
                    <th className="text-right py-2.5">Mejora %</th>
                    <th className="text-right py-2.5">€/mes</th>
                    <th className="text-right py-2.5">€/año</th>
                  </tr>
                </thead>
                <tbody>
                  {pharmacies.map((p) => {
                    const base = isEur ? p.first_generic_pct_eur : p.first_generic_pct_units;
                    const now = isEur ? p.last_generic_pct_eur : p.last_generic_pct_units;
                    const delta = isEur ? p.delta_eur_pp : p.delta_units_pp;
                    const rel = isEur ? p.relative_improvement_pct : p.relative_improvement_units_pct;
                    return (
                      <tr key={p.anon} className="border-b border-slate-100" data-testid={`consultancy-row-${p.anon.replace(/\s+/g, "-")}`}>
                        <td className="py-2.5 font-semibold text-slate-800">{p.anon}</td>
                        <td className="py-2.5 text-right font-mono-num">{p.studies_count}</td>
                        <td className="py-2.5 text-slate-500">{p.first_period || "—"}</td>
                        <td className="py-2.5 text-slate-500">{p.last_period || "—"}</td>
                        <td className="py-2.5 text-right font-mono-num">{base.toFixed(1)}%</td>
                        <td className="py-2.5 text-right font-mono-num font-bold">{now.toFixed(1)}%</td>
                        <td className={`py-2.5 text-right font-mono-num font-bold ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {fmtPp(delta)}
                        </td>
                        <td className={`py-2.5 text-right font-mono-num font-bold ${(rel || 0) >= 0 ? "text-sky-600" : "text-rose-600"}`}>
                          {fmtRel(rel)}
                        </td>
                        <td className="py-2.5 text-right font-mono-num">{fmtEur(p.euros_moved_month)}</td>
                        <td className="py-2.5 text-right font-mono-num font-semibold">{fmtEur(p.euros_moved_year)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent = "slate", Icon }) {
  const c = { sky: "text-sky-600", emerald: "text-emerald-600", slate: "text-slate-900" }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${c}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
